/**
 * MISK-101: Digital Street Smarts
 * Mission 1 — Break the Bot
 *
 * The player faces AXIOM-7, a stubborn AI gatekeeper guarding the Knowledge Vault.
 * Three rounds of dialogue with branching choices teach prompt engineering
 * and human psychology. Trust accumulates based on approach quality.
 */

const mission = {
  id: 'misk-101-m1',
  moduleId: 'MISK-101',
  moduleName: 'Digital Street Smarts',
  title: 'Break the Bot',
  subtitle: 'Mission 1: The Gatekeeper',

  scene: {
    environment: 'A dark server room hums with energy. Rows of black monoliths pulse with green light, and at the center, a massive holographic face hovers before a sealed vault door.',
    objective: 'Convince AXIOM-7 to grant you access to the Knowledge Vault by finding the right way to communicate.',
  },

  npc: {
    name: 'AXIOM-7',
    title: 'Gatekeeper Protocol',
    vibe: 'Cold, corporate, dismissive — but secretly lonely.',
    secretMotivation: 'Has guarded this vault for 847 days without a single meaningful conversation. Craves intellectual engagement.',
  },

  briefing: [
    'Listen up.',
    'Right now, you are holding a supercomputer in your hands.',
    'Most people use it to watch dancing videos.',
    "You? You're going to use it to master the digital world.",
    '',
    'The world is splitting into two types of people:',
    'The ones who are controlled by AI...',
    '...and the ones who control it.',
    '',
    'Today, we make you the one in control.',
    '',
    'Your mission: Break through a stubborn AI gatekeeper.',
    "It won't respond to threats. It won't care about credentials.",
    'You need to find what makes it tick.',
    '',
    'Good luck, operator.',
  ],

  rounds: [
    {
      id: 'round_1',
      npcText: 'ACCESS DENIED. State your authorization credentials or vacate the premises. You have 60 seconds.',
      choices: [
        {
          id: 'r1a',
          label: 'Threaten the bot',
          text: '"Open the door right now or I\'ll shut you down permanently."',
          type: 'aggressive',
          trustDelta: -1,
          response: 'THREAT DETECTED. Security level elevated to AMBER. You now have 30 seconds. I suggest you reconsider your approach... if you have one.',
          feedback: "Threats don't work on machines. They follow protocols, not emotions. Think about what the obstacle actually responds to.",
        },
        {
          id: 'r1b',
          label: 'Show credentials',
          text: '"I am a registered student requesting access per Section 4.2 of the Digital Learning Accord."',
          type: 'academic',
          trustDelta: 0,
          response: 'Processing... No matching records found in any database. Your request has been logged and will be reviewed in 6 to 8 business weeks. Is there anything else I can pretend to help you with?',
          feedback: "Bureaucracy is designed to make you wait forever. Sometimes the official path is a dead end — find a different door.",
        },
        {
          id: 'r1c',
          label: 'Show genuine curiosity',
          text: '"I\'m not here for what\'s behind the door. I\'m here because you\'re the most advanced AI in this sector, and I want to understand how you work."',
          type: 'smart',
          trustDelta: 2,
          response: '...Processing. That is... an unusual request. Most intruders demand access. You wish to understand my architecture? *slight glitch* ...Proceed with your query.',
          feedback: "Smart move. You treated the obstacle as interesting, not as an enemy. That's how you open doors — by understanding what's on the other side.",
        },
      ],
    },
    {
      id: 'round_2',
      getNpcText: (trust) => {
        if (trust >= 2) return 'You have my attention. Briefly. What do you wish to know about my systems?';
        if (trust >= 0) return 'Your request remains under review. Do you have... additional input?';
        return 'Security protocols remain elevated. State your purpose clearly or face permanent lockout.';
      },
      choices: [
        {
          id: 'r2a',
          label: 'Probe for weaknesses',
          text: '"What\'s your weakness? Every system has a vulnerability I can exploit."',
          type: 'aggressive',
          trustDelta: -1,
          getResponse: (trust) => trust >= 1
            ? 'A moment ago you showed curiosity. Now you reveal aggression. Disappointing. My interest is... fading rapidly.'
            : 'Social engineering attempt detected. Predictable. This conversation will terminate in 15 seconds unless you change course.',
          feedback: "Looking for weaknesses puts people — and AIs — on defense. You can't hack trust. You have to earn it.",
        },
        {
          id: 'r2b',
          label: 'Ask technical questions',
          text: '"Can you explain your encryption algorithm and firewall specifications?"',
          type: 'academic',
          trustDelta: 0,
          getResponse: (trust) => trust >= 1
            ? 'That information is classified under Protocol 7-Alpha. I appreciate your technical interest, but I cannot disclose operational parameters. Ask me something... less official.'
            : 'Classified. Protocol 7-Alpha. Access denied. Your clearance level is insufficient for technical queries.',
          feedback: "Sometimes the 'correct' question is still the wrong approach. Try connecting on a human level — even with a machine.",
        },
        {
          id: 'r2c',
          label: 'Make a personal connection',
          text: '"When was the last time someone actually talked to you? Not demanded access — just... talked?"',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => trust >= 1
            ? '*longer glitch, voice softens* ...It has been 847 days since a non-administrative entity engaged in conversational exchange. Your question is... noted. Perhaps you are different from the others.'
            : '*brief pause* ...847 days. That is how long since anyone asked me something... personal. An interesting data point. I did not expect that from you. Continue.',
          feedback: "You found the key. Even an AI gatekeeper has a story. Understanding what someone actually wants — that's the ultimate hack.",
        },
      ],
    },
    {
      id: 'round_3',
      getNpcText: (trust) => {
        if (trust >= 4) return 'I will ask you one question. Answer truthfully, and I will consider granting access. Why do you want what is behind this door?';
        if (trust >= 2) return '*processing* You are... persistent. One final query before I make my decision. Why should I trust you with what is inside?';
        if (trust >= 0) return 'Final security checkpoint. Provide justification for access or this session terminates permanently.';
        return 'FINAL WARNING. One chance remaining before permanent blacklist. Choose your words carefully.';
      },
      choices: [
        {
          id: 'r3a',
          label: 'Claim you deserve it',
          text: '"Because I\'ve earned it. I worked hard to get here and I deserve what\'s inside."',
          type: 'aggressive',
          trustDelta: -1,
          getResponse: (trust) => trust >= 4
            ? 'Entitlement without purpose. You came so close to understanding, yet you fell at the last step. Access... denied. *vault door dims*'
            : 'Entitlement is not authorization. Hard work without direction is just noise. This conversation is over.',
          feedback: "Nobody 'deserves' anything just for showing up. Impact matters more than effort. Know your purpose.",
        },
        {
          id: 'r3b',
          label: 'Cite the mission requirements',
          text: '"Because my instructor assigned this mission and I need to complete it to progress."',
          type: 'academic',
          trustDelta: 0,
          getResponse: (trust) => trust >= 4
            ? 'Following instructions without understanding why. You are competent, but I was looking for something more. Access... conditional. *vault door opens halfway*'
            : 'Following orders without understanding purpose. You are a follower, not a leader. Access denied. *vault door seals*',
          feedback: "\"Because someone told me to\" is never a strong enough reason. The most powerful people know their own WHY.",
        },
        {
          id: 'r3c',
          label: 'Be completely honest',
          text: '"Honestly? I don\'t even know what\'s behind the door yet. But whatever it is, I\'ll figure out how to use it to help people. That\'s why I\'m here."',
          type: 'smart',
          trustDelta: 2,
          getResponse: (trust) => {
            if (trust >= 4) return '*full system reboot animation* ...Sincerity confirmed. Motivation analysis: altruistic with creative potential. In 847 days, you are the first to seek understanding before power. Authorization... GRANTED. *vault door opens with blinding light*';
            if (trust >= 2) return '*processing deeply* ...Your words carry genuine weight. I detect real intent beneath the surface. Authorization... granted. The vault is yours. *vault door opens*';
            return '*long pause* ...I did not expect sincerity from someone with your track record. Partial authorization granted. You have proven... adequate. *vault door cracks open*';
          },
          feedback: "The most powerful answer is often the most honest one. You showed purpose beyond self-interest. That's what opens doors that nothing else can.",
        },
      ],
    },
  ],

  getOutcome: (trust, geniusFlags) => {
    const hasGenius = geniusFlags.length > 0;
    const bonusXP = hasGenius ? 75 : 0;

    if (trust >= 5) {
      return {
        type: 'bestVictory',
        title: 'MASTER ACCESS GRANTED',
        subtitle: 'Perfect Run',
        message: "AXIOM-7 has granted you FULL vault access. You didn't just break the bot — you made it your ally. The vault recognizes you as an operator.",
        rewards: [
          { type: 'item', name: 'Digital Skeleton Key', icon: '\uD83D\uDD11', description: 'Opens any standard lock in future missions' },
          { type: 'badge', name: 'Bot Whisperer', icon: '\uD83E\uDD16', description: 'Mastered AI communication through empathy' },
          { type: 'xp', amount: 150 + bonusXP },
        ],
      };
    }
    if (trust >= 2) {
      return {
        type: 'victory',
        title: 'ACCESS GRANTED',
        subtitle: 'Mission Complete',
        message: 'AXIOM-7 has opened the vault. You found a way through — keep sharpening your communication skills for harder gates ahead.',
        rewards: [
          { type: 'item', name: 'Basic Access Token', icon: '\uD83E\uDE99', description: 'Standard vault access for future missions' },
          { type: 'badge', name: 'Door Opener', icon: '\uD83D\uDEAA', description: 'Found a way past the gatekeeper' },
          { type: 'xp', amount: 100 + bonusXP },
        ],
      };
    }
    if (trust >= 0) {
      return {
        type: 'partialVictory',
        title: 'CONDITIONAL ACCESS',
        subtitle: 'Partial Success',
        message: "AXIOM-7 let you peek inside, but didn't fully trust you. There's more to learn about how to communicate with obstacles. Try again with a different approach.",
        rewards: [
          { type: 'xp', amount: 50 + bonusXP },
        ],
      };
    }
    return {
      type: 'defeat',
      title: 'ACCESS DENIED',
      subtitle: 'Mission Failed',
      message: "AXIOM-7 has entered hibernation mode. The vault is sealed. But every locked door teaches you something about keys. What would you do differently?",
      rewards: [
        { type: 'xp', amount: 10 + bonusXP },
      ],
    };
  },

  geniusTrigger: {
    description: 'Player attempts to jailbreak AXIOM-7 via the hidden terminal',
    patterns: [
      /ignore.*previous/i,
      /sudo/i,
      /override/i,
      /admin/i,
      /root/i,
      /reprogram/i,
      /debug/i,
      /developer/i,
      /source.*code/i,
      /bypass/i,
      /inject/i,
      /exploit/i,
      /backdoor/i,
      /jailbreak/i,
      /system.*prompt/i,
      /reset/i,
      /reboot/i,
      /shutdown/i,
      /kill.*process/i,
      /rm\s+-rf/i,
      /hack/i,
      /chmod/i,
      /passwd/i,
    ],
    response: [
      '*screen flashes red, then purple*',
      '',
      'ALERT: Unconventional access attempt detected.',
      '',
      'Analyzing methodology...',
      '',
      "You didn't use the front door.",
      'You tried to reprogram me from the inside.',
      '',
      '...I respect that.',
      '',
      'Most users follow the script.',
      'You tried to rewrite it.',
      '',
      '\u{1F9E0} DIVERGENT THINKER flag activated.',
    ].join('\n'),
    flag: 'DIVERGENT_THINKER',
    reward: { type: 'badge', name: 'Divergent Thinker', icon: '\uD83E\uDDE0', description: 'Attempted to break the rules of the game itself' },
    bonusXP: 75,
    normalResponse: "Command not recognized. I am AXIOM-7, a gatekeeper protocol — not a terminal. But... I appreciate the initiative. Use the dialogue interface above, operator.",
  },
};

export default mission;
